<template>
  <CommonLayout>
    <div class="recharge-form">
      <el-card>
        <template #header>
          <div class="card-header">
            <el-icon><Wallet /></el-icon>
            <span>充值</span>
          </div>
        </template>
        <div class="card-content">
          <el-form :model="formData" ref="formRef" label-width="120px">
            <el-form-item label="手机号">
              <el-input v-model="formData.optUsername"></el-input>
            </el-form-item>
            <!-- 新增常用套餐部分 -->
            <el-form-item label="常用套餐" class="custom-package-item"> <!-- 添加自定义 class -->
              <el-tag
                :key="index"
                :type="selectedPackage === item ? 'primary' : 'info'"
                @click="selectPackage(item)"
                v-for="(item, index) in packages"
                :index="index"
              >{{ item }}</el-tag>
            </el-form-item>
            <el-form-item label="上传次数">
              <el-input v-model="formData.uploadNumDelta"></el-input>
            </el-form-item>
            <el-form-item label="下载次数">
              <el-input v-model="formData.downloadNumDelta"></el-input>
            </el-form-item>
            <el-form-item label="查询次数">
              <el-input v-model="formData.queryNumDelta"></el-input>
            </el-form-item>
            <el-form-item label="删除次数">
              <el-input v-model="formData.deleteNumDelta"></el-input>
            </el-form-item>
            <el-form-item>
              <el-button type="primary" @click="submitForm" :loading="isLoading">提交</el-button>
              <!-- 新增重置按钮 -->
              <el-button @click="resetForm">重置</el-button> 
            </el-form-item>
          </el-form>
        </div>
      </el-card>
      <!-- 充值记录卡片 -->
      <el-card style="margin-top: 20px;"> <!-- 修改：增加卡片间距 -->
        <template #header>
          <div class="card-header">
            <el-icon><Document /></el-icon>
            <span>充值记录</span>
            <el-icon @click="fetchRechargeList" :class="{ 'refresh-icon': true, 'loading': isRefreshing }">
              <!-- 根据刷新状态显示不同图标 -->
              <template v-if="isRefreshing">
                <Loading />
              </template>
              <template v-else>
                <Refresh />
              </template>
            </el-icon>
          </div>
        </template>
        <div class="card-content">
          <el-table
            :data="rechargeList"
            :height="tableHeight"
            :max-height="tableMaxHeight"
            stripe
          >
            <!-- 更新表格列 -->
            <el-table-column prop="username" label="充值手机号"></el-table-column>
            <el-table-column prop="uploadNum" label="上传次数"></el-table-column>
            <el-table-column prop="downloadNum" label="下载次数"></el-table-column>
            <el-table-column prop="queryNum" label="查询次数"></el-table-column>
            <el-table-column prop="deleteNum" label="删除次数"></el-table-column>
            <el-table-column prop="remark" label="备注"></el-table-column>
            <el-table-column prop="createTime" label="创建时间" width="200"></el-table-column>
          </el-table>
          <el-pagination
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
            :current-page="currentPage"
            :page-sizes="[10, 20, 30]"
            :page-size="pageSize"
            layout="total, sizes, prev, pager, next, jumper"
            :total="totalRecords"
          >
          </el-pagination>
        </div>
      </el-card>
    </div>
  </CommonLayout>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { ElMessage } from 'element-plus';
import service from '@/utils/http';
import { generateTimestamp, generateToken } from '@/utils/crypto';
import { API_PATHS } from '@/config/api';
import CommonLayout from '@/components/CommonLayout.vue';
import { Wallet, Document, Refresh, Loading } from '@element-plus/icons-vue'; // 引入新图标
import { validateNotEmpty, validatePhoneNumber } from '@/utils/validation';

const userInfoStr = localStorage.getItem('userInfo')
const userInfo = userInfoStr ? JSON.parse(userInfoStr) : null
const userId = ref(userInfo ? userInfo.userId : null)

const formRef = ref(null);
const formData = ref({
  optUsername: '',
  uploadNumDelta: '',
  downloadNumDelta: '',
  queryNumDelta: '',
  deleteNumDelta: ''
});
const isLoading = ref(false);
const isRefreshing = ref(false); // 新增刷新状态变量

// 新增套餐相关数据
const packages = ref(['少量', '中量', '大量', '自定义']);
const selectedPackage = ref('少量');

// 点击套餐标签的处理函数
const selectPackage = (packageName) => {
  selectedPackage.value = packageName;
  switch (packageName) {
    case '少量':
      formData.value.uploadNumDelta = '10';
      formData.value.downloadNumDelta = '10';
      formData.value.queryNumDelta = '100';
      formData.value.deleteNumDelta = '10';
      break;
    case '中量':
      formData.value.uploadNumDelta = '100';
      formData.value.downloadNumDelta = '100';
      formData.value.queryNumDelta = '500';
      formData.value.deleteNumDelta = '100';
      break;
    case '大量':
      formData.value.uploadNumDelta = '1000';
      formData.value.downloadNumDelta = '1000';
      formData.value.queryNumDelta = '5000';
      formData.value.deleteNumDelta = '1000';
      break;
    case '自定义':
      formData.value.uploadNumDelta = '';
      formData.value.downloadNumDelta = '';
      formData.value.queryNumDelta = '';
      formData.value.deleteNumDelta = '';
      break;
  }
};

// 初始化默认套餐
selectPackage(selectedPackage.value);

const submitForm = async () => {
  // 表单校验
  if (!validateNotEmpty(formData.value.optUsername)) {
    ElMessage.error('手机号不能为空');
    return;
  }
  if (!validatePhoneNumber(formData.value.optUsername)) {
    ElMessage.error('手机号格式不正确');
    return;
  }
  if (!validateNotEmpty(formData.value.uploadNumDelta)) {
    ElMessage.error('上传次数不能为空');
    return;
  }
  if (!validateNotEmpty(formData.value.downloadNumDelta)) {
    ElMessage.error('下载次数不能为空');
    return;
  }
  if (!validateNotEmpty(formData.value.queryNumDelta)) {
    ElMessage.error('查询次数不能为空');
    return;
  }
  if (!validateNotEmpty(formData.value.deleteNumDelta)) {
    ElMessage.error('删除次数不能为空');
    return;
  }

  isLoading.value = true; // 开始加载
  try {
    const timestamp = generateTimestamp();

    // 构建完整的请求数据
    const requestData = {
      ...formData.value,
      userId: userId.value,
      timestamp
    };

    // 使用完整的请求数据生成 token
    const token = generateToken(requestData);

    // 将 token 添加到请求数据中
    requestData.token = token;

    await service.post(
      `${API_PATHS.recharge}`,
      requestData
    );
    ElMessage.success('充值成功');
    await fetchRechargeList(); // 充值成功后刷新充值记录列表
  } catch (error) {
    ElMessage.error(error.message || '充值失败');
  } finally {
    isLoading.value = false; // 结束加载
  }
};

// 新增重置表单方法
const resetForm = () => {
  formData.value = {
    optUsername: '',
    uploadNumDelta: '',
    downloadNumDelta: '',
    queryNumDelta: '',
    deleteNumDelta: ''
  };
  // 重置套餐选择
  selectedPackage.value = '自定义';
  selectPackage(selectedPackage.value);
};

// 新增充值记录相关数据
const rechargeList = ref([]);
const currentPage = ref(1);
const pageSize = ref(10);
const totalRecords = ref(0);
const tableHeight = ref(400);
const tableMaxHeight = ref(600);

const fetchRechargeList = async () => {
  isRefreshing.value = true; // 开始刷新，设置刷新状态
  try {
    const timestamp = generateTimestamp()
    const params = {
      userId: userId.value,
      currentPage: currentPage.value,
      size: pageSize.value,
      timestamp,
      token: generateToken({ userId: userId.value, currentPage: currentPage.value, size: pageSize.value, timestamp })
    }

    const response = await service.get(
      `${API_PATHS.rechargeList}`,
      { params }
    )
    rechargeList.value = response.data.list || []
    totalRecords.value = response.data.size
  } catch (error) {
    ElMessage.error(error.message || '获取充值记录失败');
  } finally {
    isRefreshing.value = false; // 刷新完成，重置刷新状态
  }
};

const handleSizeChange = (newSize) => {
  pageSize.value = newSize;
  fetchRechargeList();
};

const handleCurrentChange = (newPage) => {
  currentPage.value = newPage;
  fetchRechargeList();
};

onMounted(() => {
  fetchRechargeList();
});
</script>

<style scoped>
.recharge-form {
  padding: 20px;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 500;
}

.card-header .el-icon {
  font-size: 20px;
  color: #409EFF;
}

.card-content {
  padding: 20px;
  background-color: #fff;
  border-radius: 4px;
  overflow-x: auto; /* 添加水平滚动条 */
}

/* 修改后的样式 */
.custom-package-item .el-tag {
  margin-right: 8px; /* 设置标签右侧间隔 */
  cursor: pointer; /* 鼠标移上去变为可点击状态 */
}

/* 刷新图标样式 */
.refresh-icon {
  margin-left: auto; /* 靠右对齐 */
  cursor: pointer; /* 鼠标指针样式 */
  color: #999; /* 图标颜色 */
  transition: color 0.3s; /* 颜色过渡效果 */
}

.refresh-icon.loading {
  animation: spin 1s linear infinite; /* 加载动画 */
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.refresh-icon:hover {
  color: #409EFF; /* 鼠标悬停时图标颜色 */
}
</style>